/**
 * WHAT A BANNER SAYS, AS DATA (the banner band, PV5-8:D part 1, 2026-10-14).
 *
 * Every source in the band (GlobalBannerCenter.tsx) used to hand the band only a drawing: React nodes built from its own
 * styled spans and buttons. The new design draws the band with its own kit, and it may not draw today's styled parts
 * (E0d), so each source now also says WHAT its notice is: the words, the phone's shorter words, and the one action with
 * its label. Today's band keeps drawing each source's `content`, which the source builds from this same notice, so the
 * two designs cannot say different things: the words live once, here.
 *
 * On the first download (the band's sources are), so it imports nothing.
 */

/** A piece of a notice's words: text, or the pr0 unit where an amount of pr0 is named (drawn by each design's glyph). */
export type BannerWordPiece = string | { readonly pr0: true };

/** A notice's words: one string, or text with the pr0 unit inside it. */
export type BannerWords = string | readonly BannerWordPiece[];

/** The pr0 unit inside a notice's words. */
export const PR0_PIECE: BannerWordPiece = Object.freeze({ pr0: true as const });

/**
 * The notice's one action. Exactly one of `onPress` (a button), `to` (a page inside the app) and `href` (an address) is
 * set; each design draws it as its own button or link.
 */
export interface BannerAction {
  readonly label: string;
  /** The phone's label, where the full one would not fit beside the short words. Absent: the label at every width. */
  readonly shortLabel?: string;
  readonly onPress?: () => void;
  readonly to?: string;
  readonly href?: string;
  /** A press that cannot act now (a link already sent, a send in flight). */
  readonly disabled?: boolean;
  /** The marker today's tests and the live suite read on the action (tests-prod/core/selectors.cjs). */
  readonly testId?: string;
  /** The marker on the phone's action, where it differs. */
  readonly shortTestId?: string;
  /** Today's band draws this action as an urgent one (the out-of-pr0 "top up"); the new design reads the tone instead. */
  readonly urgent?: boolean;
}

/** What a banner says. */
export interface BannerNotice {
  readonly words: BannerWords;
  /** The phone's words (the band shows them under the narrow tier, a tap away from the whole notice). */
  readonly shortWords?: BannerWords;
  readonly action?: BannerAction;
  /** The marker today's tests and the live suite read on the words. */
  readonly wordsTestId?: string;
  /** The marker on the phone's words, where it differs. */
  readonly shortWordsTestId?: string;
  /** A data attribute today's tests read on the words (`data-billing-paused-banner`), kept on both designs' words. */
  readonly wordsMarker?: string;
}

/** The words as plain text, the pr0 unit read as "pr0" (what assistive technology reads; tests compare it). */
export function bannerWordsText(words: BannerWords): string {
  if (typeof words === 'string') return words;
  return words.map((piece) => (typeof piece === 'string' ? piece : 'pr0')).join('');
}

/** The words a width shows: the phone's where it has its own, else the whole notice. */
export function wordsFor(notice: BannerNotice, short: boolean): BannerWords {
  return short && notice.shortWords !== undefined ? notice.shortWords : notice.words;
}

/** The action's label a width shows. */
export function actionLabelFor(action: BannerAction, short: boolean): string {
  return short && action.shortLabel !== undefined ? action.shortLabel : action.label;
}

/**
 * The band's own words, not any notice's: the tap that opens the phone's words, a dot's name and the dismiss. One table
 * for both designs' bands, so the two name their controls alike.
 */
export const BAND_WORDS = {
  expand: 'show the full notice',
  dismiss: 'Dismiss',
  showNotice: (place: number, count: number): string => `show notice ${place} of ${count}`,
};
